import {
  type ClassifierRule,
  type ClassifierRuleRecord,
  type ClassifierRuleType,
  type SignalAdminConfig,
  type SignalAdminDraft,
  type SignalAdminRegistry,
  type SignalDefinition,
  type SignalTier,
} from "./types";

/** Rule Classifier helpers. The backend (utils/signal_rules.py) is the
 * authority — it re-checks every rule on apply; these mirror its checks so
 * the editor can say what's wrong before then. */

export const TYPE_HINT: Record<ClassifierRuleType, string> = {
  duplicate:
    "Two signals say the same thing. While the lead fires, the others don't show.",
  supersede:
    "While the lead fires, the others are covered — kept open, not shown — and come back when it clears.",
};

export const TYPE_CLASS: Record<ClassifierRuleType, string> = {
  duplicate: "bg-rose-50 text-rose-700",
  supersede: "bg-indigo-50 text-indigo-700",
};

export const STATUS_CLASS: Record<ClassifierRule["status"], string> = {
  active: "bg-emerald-50 text-emerald-700",
  proposed: "bg-sky-50 text-sky-700",
  off: "bg-gray-100 text-gray-600",
};

const TIER_RANK: Record<SignalTier, number> = {
  critical: 0,
  warning: 1,
  opp: 2,
  maint: 3,
};

/** "#14" / "P5" — the admin Signals list's # column. */
export const signalTag = (d: Pick<SignalDefinition, "order">) =>
  d.order.startsWith("P") ? d.order : `#${d.order}`;

const orderKey = (order: string): [number, number] =>
  /^\d+$/.test(order)
    ? [0, Number(order)]
    : [1, Number(order.replace(/\D/g, "")) || 0];

/** Highest tier, then catalogue order — how the backend picks a lead. */
export const pickLead = (
  members: string[],
  byId: Record<string, SignalDefinition>
): string => {
  const known = members.filter(m => byId[m]);
  const sorted = [...known].sort((a, b) => {
    const ta = TIER_RANK[byId[a].tier] - TIER_RANK[byId[b].tier];
    if (ta !== 0) return ta;
    const [ga, na] = orderKey(byId[a].order);
    const [gb, nb] = orderKey(byId[b].order);
    return ga - gb || na - nb;
  });
  return sorted[0] ?? members[0] ?? "";
};

/** Where a signal's CTA opens now (route kind + path). */
/** Problems with a rule as the backend would see them when switched on. */
export const ruleProblems = (rule: ClassifierRuleRecord): string[] => {
  const out: string[] = [];
  if (rule.members.length < 2) out.push("Pick at least two signals.");
  if (rule.members.length > 6) out.push("A rule can hold at most 6 signals.");
  if (!rule.members.includes(rule.lead))
    out.push("The lead must be one of the rule's signals.");
  return out;
};

/** Active Duplicate/Supersede rules that cover each other in a loop. */
export const loopProblems = (
  rules: ClassifierRuleRecord[],
  registry: SignalAdminRegistry
): string[] => {
  const byId = Object.fromEntries(registry.definitions.map(d => [d.id, d]));
  const edges = new Map<string, Set<string>>();
  rules
    .filter(r => r.status === "active")
    .forEach(r =>
      r.members
        .filter(m => m !== r.lead)
        .forEach(m =>
          edges.set(
            r.lead,
            new Set([...Array.from(edges.get(r.lead) ?? []), m])
          )
        )
    );
  const out = new Set<string>();
  const seen = new Set<string>();
  const visit = (node: string, path: string[]) => {
    if (path.includes(node)) {
      const loop = [...path.slice(path.indexOf(node)), node];
      const key = Array.from(new Set(loop)).sort().join("|");
      if (seen.has(key)) return;
      seen.add(key);
      out.add(
        `These rules cover each other in a loop: ${loop
          .map(n => (byId[n] ? `${signalTag(byId[n])} ${byId[n].title}` : n))
          .join(" → ")}.`
      );
      return;
    }
    Array.from(edges.get(node) ?? []).forEach(next =>
      visit(next, [...path, node])
    );
  };
  Array.from(edges.keys()).forEach(start => visit(start, []));
  return Array.from(out);
};

const sameRecord = (a: ClassifierRuleRecord, b: ClassifierRuleRecord) =>
  a.type === b.type &&
  a.lead === b.lead &&
  a.status === b.status &&
  (a.note ?? "") === (b.note ?? "") &&
  [...a.members].sort().join() === [...b.members].sort().join();

export const toRecord = (r: ClassifierRuleRecord): ClassifierRuleRecord => ({
  type: r.type,
  lead: r.lead,
  members: [...r.members],
  status: r.status,
  note: r.note ?? "",
});

export interface DraftRule extends ClassifierRule {
  /** Differs from what's applied — waiting for Review and apply. */
  unapplied: boolean;
}

/** The rules as this draft has them, with where each one came from. */
export const draftRules = (
  registry: SignalAdminRegistry,
  config: SignalAdminConfig,
  draft: SignalAdminDraft
): DraftRule[] => {
  const applied = config.classifier ?? {};
  const staged = draft.classifier ?? {};
  const unapplied = (id: string) =>
    JSON.stringify(applied[id] ?? null) !== JSON.stringify(staged[id] ?? null);
  const out: DraftRule[] = [];
  registry.classifier.rules.forEach(rule => {
    const copy = staged[rule.id];
    if (rule.code) {
      const record = copy ?? toRecord(rule.code);
      out.push({
        ...rule,
        ...record,
        source: copy && !sameRecord(copy, rule.code) ? "changed" : "code",
        unapplied: unapplied(rule.id),
      });
    } else if (copy) {
      out.push({
        ...rule,
        ...copy,
        source: "added",
        unapplied: unapplied(rule.id),
      });
    }
  });
  Object.entries(staged)
    .filter(([id]) => !registry.classifier.rules.some(r => r.id === id))
    .forEach(([id, record]) =>
      out.push({
        id,
        ...record,
        source: "added",
        code: null,
        errors: [],
        unapplied: true,
      })
    );
  return out;
};

/** Stage `record` for rule `id`: a copy equal to the code file is dropped,
 * so a rule set back to its shipped form reads "Code default" again. */
export const stageRule = (
  draft: SignalAdminDraft,
  id: string,
  record: ClassifierRuleRecord | null,
  code: ClassifierRuleRecord | null
): SignalAdminDraft => {
  const classifier = { ...(draft.classifier ?? {}) };
  if (record === null || (code && sameRecord(record, code)))
    delete classifier[id];
  else classifier[id] = toRecord(record);
  return { ...draft, classifier };
};

/** The next free id for an admin-added rule: A1, A2, … */
export const nextRuleId = (rules: { id: string }[]) => {
  const taken = new Set(rules.map(r => r.id));
  let n = 1;
  while (taken.has(`A${n}`)) n += 1;
  return `A${n}`;
};

/** The draft's unapplied classifier changes, as the apply payload sends
 * them ({rule_id: record | null}). */
export const classifierChanges = (
  config: SignalAdminConfig,
  draft: SignalAdminDraft
): Record<string, ClassifierRuleRecord | null> => {
  const applied = config.classifier ?? {};
  const staged = draft.classifier ?? {};
  const out: Record<string, ClassifierRuleRecord | null> = {};
  Array.from(
    new Set([...Object.keys(applied), ...Object.keys(staged)])
  ).forEach(id => {
    if (
      JSON.stringify(applied[id] ?? null) !== JSON.stringify(staged[id] ?? null)
    )
      out[id] = staged[id] ?? null;
  });
  return out;
};

export const TYPE_ORDER: ClassifierRuleType[] = ["supersede", "duplicate"];

export const TYPE_SECTION: Record<ClassifierRuleType, string> = {
  supersede:
    "While the lead fires, the others are kept open but hidden. They come back when it clears.",
  duplicate:
    "Two signals that say the same thing. Only the lead shows while it fires.",
};

/** Tier dot colours for signal chips. */
export const TIER_DOT: Record<SignalTier, string> = {
  critical: "bg-red-600",
  warning: "bg-amber-500",
  opp: "bg-emerald-600",
  maint: "bg-slate-400",
};
